import React, { useEffect, useState } from 'react';

import { Home } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import FormContent from './FormContent';
import {
  deleteLOBPushNotification,
  getCountForPushNotification,
  getLOBPushNotification,
  updateLOBPushNotification,
  sendPushNotification,
  getList,
} from './operations';
import { Breadcrumbs, ModalConfirmation, SubHeader, Footer } from '../../../components';
import './styles.scoped.css';

const utc = require('dayjs/plugin/utc');

dayjs.extend(utc);

const editLOBPushNotifications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(state => state.admin?.user);
  const { id } = useParams();
  const [lobs, setLOBs] = useState([]);
  const [recipientStatuses, setRecipientStatuses] = useState([]);
  const [messageCharCount, setMessageCharCount] = useState(0);
  const messageCharLimit = 150;
  const [subjectCharCount, setSubjectCharCount] = useState(0);
  const subjectCharLimit = 50;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoadingLobs, setIsLoadingLOBs] = useState(false);
  const [isLoadingRecipientStatus, setIsLoadingRecipientStatus] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDriversCountModal, setShowDriversCountModal] = useState(false);
  const [form, setForm] = useState({
    id: '',
    type: 'LOB',
    subject: '',
    message: '',
    url: '',
    status: '',
    lob: '',
    branch: null,
    recipientStatus: '',
    approvedBy: user?.name,
    approvedAt: null,
    createdBy: user?.name,
    createdAt: dayjs().utc().format(),
    sentOn: null,
  });
  const [driversToBeNotified, setDriversToBeNotifed] = useState(0);
  const [driversNotified, setDriversNotifed] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingNotifiedNotifications, setIsLoadingNotifiedNotifications] = useState(false);
  const [errors, setErrors] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showExecutionTimeoutException, setShowExecutionTimeoutException] = useState(false);

  const handleGetRecipientStatuses = async () => {
    setIsLoadingRecipientStatus(true);
    try {
      const responses = await getList('driver-status');
      setRecipientStatuses(responses);
    } catch (e) {
      console.log('e', e);
    }
    setIsLoadingRecipientStatus(false);
  };

  const getNotification = async anId => {
    try {
      const response = await getLOBPushNotification(anId);
      setForm({
        ...form,
        id: response?.id,
        type: response?.type,
        subject: response?.subject,
        message: response?.message,
        url: response?.url,
        status: response?.status,
        lob: response?.lob,
        branch: response?.branch,
        recipientStatus: response?.recipientStatus,
        approvedBy: response?.name,
        approvedAt: response?.approvedAt,
        sentOn: response?.sentOn,
        createdBy: response?.createdBy,
        createdAt: response?.createdAt,
      });
    } catch (e) {
      console.log('e', e);
    }
  };

  const handleGetLOB = async () => {
    setIsLoadingLOBs(true);
    try {
      const responses = await getList('line-of-business');
      setLOBs(responses);
    } catch (e) {
      console.log('e', e);
    }
    setIsLoadingLOBs(false);
  };

  const customStyles = width => ({
    control: provided => ({
      ...provided,
      width: width,
      height: '30px',
      minWidth: '300px',
    }),
    menuList: provided => ({
      ...provided,
      maxHeight: '200px',
    }),
    menu: provided => ({
      ...provided,
      zIndex: 100000,
    }),
    multiValue: provided => ({
      ...provided,
      backgroundColor: '#da0f2e',
    }),
    multiValueLabel: provided => ({
      ...provided,
      color: '#ffffff',
    }),
    multiValueRemove: provided => ({
      ...provided,
      color: '#ffffff',
    }),
  });

  const fieldsAreValid = () => {
    let valid = true;
    let formErrorsCopy = { ...errors };

    const requiredFields = ['lob', 'message', 'recipientStatus', 'subject'];

    if (!form?.lob) {
      formErrorsCopy = {
        ...formErrorsCopy,
        lob: 'Please provide a valid value.',
      };
      valid = false;
    } else {
      formErrorsCopy = {
        ...formErrorsCopy,
        lob: '',
      };
      valid = true;
    }

    if (!form?.recipientStatus) {
      formErrorsCopy = {
        ...formErrorsCopy,
        recipientStatus: 'Please provide a valid value.',
      };
      valid = false;
    } else {
      formErrorsCopy = {
        ...formErrorsCopy,
        recipientStatus: '',
      };
      valid = true;
    }

    requiredFields.forEach(field => {
      if (!form[field] || form[field] === '') {
        formErrorsCopy = {
          ...formErrorsCopy,
          [field]: 'Please provide a valid value.',
        };
        valid = false;
      }
    });

    setErrors(formErrorsCopy);

    return valid;
  };

  const handleCheckbox = e => {
    if (e.target.checked === true) {
      setForm({
        ...form,
        status: 'Sent',
        sentOn: dayjs().utc().format(),
        approvedBy: user.name,
        approvedAt: dayjs().utc().format(),
      });
    } else {
      setForm({
        ...form,
        status: 'Pending',
      });
    }
  };

  const removeLOBPushNotification = async () => {
    try {
      await deleteLOBPushNotification(id);
    } catch (e) {
      console.log('e', e);
    }
  };

  const confirmLOBNotification = async () => {
    setIsLoading(true);

    if (fieldsAreValid()) {
      if (form?.status === 'Pending') {
        try {
          await updateLOBPushNotification(form);
          navigate(-1);
        } catch (e) {
          setShowErrorModal(true);
          console.log('error', e);
        }
      }

      if (form?.status === 'Sent') {
        try {
          await getDriversToBeNotifiedForPushNotifications(form);
        } catch (e) {
          console.log('error', e);
        }
      }
    }

    setIsLoading(false);
  };

  const getDriversToBeNotifiedForPushNotifications = async aForm => {
    try {
      const response = await getCountForPushNotification(aForm);
      if (response?.responseCode === 200) {
        setIsLoading(false);
        setDriversToBeNotifed(response?.driverCount);
        setShowDriversCountModal(true);
      }

      if (response?.responseCode === 404) {
        setIsLoading(false);
        setDriversToBeNotifed(response?.driverCount);
        setShowDriversCountModal(true);
        setForm({
          ...aForm,
          status: 'Pending',
        });
      }
    } catch (e) {
      setShowErrorModal(true);
      console.log('error', e);
    }
  };

  const sendBulkPushNotification = async aForm => {
    setIsLoadingNotifiedNotifications(true);

    try {
      const response = await sendPushNotification(aForm);

      if (
        response?.data?.publishPushNotification?.responseCode === 200 &&
        response?.data?.publishPushNotification?.driverCount > 0
      ) {
        setShowDriversCountModal(false);
        setDriversNotifed(response?.data?.publishPushNotification?.driverCount);
        setIsLoadingNotifiedNotifications(false);
        setShowConfirmationModal(true);
        await updateLOBPushNotification(aForm);
      }

      if (
        response?.data?.publishPushNotification?.responseCode === 404 &&
        response?.data?.publishPushNotification?.responseMessage !== 'Success'
      ) {
        setDriversToBeNotifed(response?.data?.publishPushNotification?.driverCount);
        setShowDriversCountModal(true);
        setIsLoadingNotifiedNotifications(false);
        setForm({
          ...aForm,
          status: 'Pending',
        });
      }

      if (response?.errors !== undefined && response?.errors[0]?.errorType === 'Lambda:ExecutionTimeoutException') {
        setIsLoading(false);
        setIsLoadingNotifiedNotifications(false);
        setShowConfirmationModal(false);
        setShowExecutionTimeoutException(true);
      }
    } catch (e) {
      console.log('error', e);
    }
  };

  const convertArrayToObject = items => {
    const arr = [];
    if (items) {
      items.split(', ').forEach(i => arr.push({ label: i, value: i }));
    }
    return arr;
  };

  const setField = (fields, value) => {
    if (Array.isArray(fields)) {
      const itemsToEdit = {};
      const newErrors = { ...errors };
      fields.forEach(field => {
        itemsToEdit[field?.fieldName] = field?.fieldValue;
        if (errors && !!errors[field.fieldName]) delete newErrors[field?.fieldName];
      });
      setForm({
        ...form,
        ...itemsToEdit,
      });

      setErrors({
        ...newErrors,
      });
    } else {
      setForm({
        ...form,
        [fields]: value,
      });
      if (errors && !!errors[fields]) {
        setErrors({
          ...errors,
          [fields]: null,
        });
      }
    }
  };

  const handleCancel = () => {
    setForm({});
    navigate(-1);
  };

  useEffect(() => {
    if (form?.subject || form?.message) {
      setSubjectCharCount(form?.subject.trim().length);
      setMessageCharCount(form?.message.trim().length);
    }
  }, [form?.subject, form?.message]);

  useEffect(() => {
    if (id) {
      getNotification(id);
    }
  }, [id]);

  useEffect(() => {
    handleGetLOB();
    handleGetRecipientStatuses();
  }, []);

  return (
    <>
      <SubHeader
        text="Push Notifications"
        buttons={[
          { icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/push-notifications') },
        ]}
      />

      <Breadcrumbs
        wrapStyle={{ margin: 0 }}
        leftTitle="PN Home"
        middleTitle="LOB Push Notifications"
        midItemAction={() => navigate(`../push-notifications/lob-push-notifications`, { replace: true })}
        rightTitle={`${location.pathname.includes('edit') ? 'Edit LOB Notification' : 'View LOB Notification'}`}
        route="/push-notifications"
        style={{ marginLeft: 30 }}
      />

      <FormContent
        confirmLOBNotification={confirmLOBNotification}
        customStyles={customStyles}
        errors={errors}
        lobs={lobs}
        location={location}
        isLoadingLOBS={isLoadingLobs}
        headerTitle={`${'Edit LOB Push Notification'}`}
        handleCheckbox={handleCheckbox}
        recipientStatuses={recipientStatuses}
        isLoadingRecipientStatus={isLoadingRecipientStatus}
        form={form}
        isLoading={isLoading}
        setForm={setForm}
        messageCharCount={messageCharCount}
        messageCharLimit={messageCharLimit}
        setField={setField}
        setMessageCharCount={setMessageCharCount}
        setSubjectCharCount={setSubjectCharCount}
        subjectCharCount={subjectCharCount}
        subjectCharLimit={subjectCharLimit}
        convertArrayToObject={convertArrayToObject}
        handleCancel={handleCancel}
        setShowDeleteModal={setShowDeleteModal}
      />

      <ModalConfirmation
        title="Error"
        body="There was an error with the push notification. Please contact support."
        primaryButtonTitle="Close"
        onClick={() => {
          setShowErrorModal(false);
          navigate(-1);
        }}
        isOpen={showErrorModal}
        primaryButton
      />

      <ModalConfirmation
        title="Send Confirmation"
        body={`This will be sent to ${driversToBeNotified} drivers`}
        isOpen={showDriversCountModal}
        primaryButton={driversToBeNotified > 0}
        optionalClose
        primaryButtonTitle="Send"
        secondaryButtonTitle="Cancel"
        isLoading={isLoadingNotifiedNotifications}
        onClick={async () => {
          sendBulkPushNotification(form);
        }}
        secondaryOnClick={() => {
          setForm({
            ...form,
            status: 'Pending',
          });
          setShowDriversCountModal(false);
        }}
      />

      <ModalConfirmation
        title="Send Confirmation"
        body={`This was sent to ${driversNotified} drivers`}
        isOpen={showConfirmationModal}
        primaryButton={driversNotified > 0}
        primaryButtonTitle="Ok"
        onClick={async () => {
          setShowConfirmationModal(false);
          navigate(-1);
        }}
      />

      <ModalConfirmation
        title="Error"
        body="Execution Timeout Exception."
        primaryButtonTitle="Try Again"
        secondaryButtonTitle="Cancel"
        optionalClose
        onClick={() => {
          setShowExecutionTimeoutException(false);
          sendBulkPushNotification(form);
        }}
        secondaryOnClick={() => {
          setForm({
            ...form,
            status: 'Pending',
          });
          setShowExecutionTimeoutException(false);
        }}
        isOpen={showExecutionTimeoutException}
        primaryButton
      />

      <ModalConfirmation
        title="Please Confirm"
        body="Are you sure you want to permanently delete this push notification?"
        isOpen={showDeleteModal}
        primaryButton
        optionalClose
        primaryButtonTitle="Ok"
        secondaryButtonTitle="Cancel"
        onClick={() => {
          setShowDeleteModal(false);
          removeLOBPushNotification(id);
          navigate(-1);
        }}
        secondaryOnClick={() => {
          setShowDeleteModal(false);
        }}
      />

      <Footer />
    </>
  );
};

export default editLOBPushNotifications;
